import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useIntl } from 'react-intl';
import { useQueryParam, useRouter } from 'react-resource-router';

import Avatar from '@atlaskit/avatar';
import AddIcon from '@atlaskit/icon/core/add';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import ClockIcon from '@atlaskit/icon/core/clock';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import {
  SearchAnchor,
  SearchDialog,
  ResultContainer,
  SearchResultSection,
  SearchResult,
  SearchInput,
  KeyboardHighlightProvider,
  SearchFooter,
} from '@atlassian/search-dialog';

import { ComponentLink } from 'src/components/component-link';
import { formatRelativeDate } from 'src/components/date/utils/format-relative-date';
import navigationUrls from 'src/components/navigation/src/urls';
import { ROUTE_NAME } from 'src/router/routes/types';
import { useRecentSearches } from 'src/sections/search/hooks/use-recent-searches';
import { filterRecentSearches } from 'src/sections/search/utils/filter-recent-searches';

import { BitbucketSearchLink } from './bitbucket-search-link';
import { getActiveFilter } from './get-active-filter';
import { messages } from './messages';
import type { ActiveFilter, SearchFilter } from './types';
import {
  RECENT_REPOS_DISPLAY_LIMIT,
  useRecentlyUpdatedRepositories,
} from './use-recently-updated-repositories';
import {
  REPO_FILTER_OPTIONS_DISPLAY_LIMIT,
  useRepoFilterOptions,
} from './use-repo-filter-options';

export const MAX_VISIBLE_RECENT_SEARCHES = 3;
const SEARCH_RESULT_HEIGHT_PX = 32;

const REPO_FILTER: SearchFilter = {
  label: 'repo:',
  description: messages.filterDescriptionRepo,
};

export const SEARCH_FILTERS: SearchFilter[] = [
  REPO_FILTER,
  { label: 'language:', description: messages.filterDescriptionLanguage },
];

// Filters that show a scoped suggestion submenu when the cursor is inside them.
const FILTERS_WITH_OPTIONS: SearchFilter[] = [REPO_FILTER];

const containerStyles = xcss({
  position: 'relative',
  width: '100%',
});

const makeLoadingContainerStyles = (itemCount: number) =>
  xcss({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: `${SEARCH_RESULT_HEIGHT_PX * itemCount}px`,
  });

const repoLoadingContainerStyles = makeLoadingContainerStyles(
  RECENT_REPOS_DISPLAY_LIMIT
);
const repoFilterLoadingContainerStyles = makeLoadingContainerStyles(
  REPO_FILTER_OPTIONS_DISPLAY_LIMIT
);

export interface BitbucketSearchDialogProps {
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  workspaceSlug?: string;
  onSearch?: (query: string) => void;
}

export const BitbucketSearchDialog: React.FC<BitbucketSearchDialogProps> = ({
  isExpanded,
  setIsExpanded,
  workspaceSlug,
  onSearch,
}) => {
  const intl = useIntl();
  const [{ route }] = useRouter();

  const [qParam = ''] = useQueryParam('q');
  // Only pre-populate the search input from ?q= when we're on the search
  // results page.  Other pages use ?q= for unrelated purposes (e.g. table
  // filtering) and the dialog should start empty there.
  const initialQuery = route.name === ROUTE_NAME.WORKSPACE_SEARCH ? qParam : '';
  const { recentSearches } = useRecentSearches(workspaceSlug ?? '');
  const [query, setQuery] = useState(initialQuery);
  const [activeFilter, setActiveFilter] = useState<ActiveFilter | null>(null);

  const { repositories: recentRepositories, isLoading: isLoadingRepositories } =
    useRecentlyUpdatedRepositories(workspaceSlug ?? '', isExpanded, query);

  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<number>();

  // We're currently only checking for whether the repo filter is active,
  // so we only check whether there is an active filter.
  // If we add more filters in the future and want to check for specific filter types,
  // we can check activeFilter?.filter.label instead.
  const isScopedToRepo = activeFilter !== null && !activeFilter.value;

  const { options: repoFilterOptions, isLoading: isLoadingRepoFilterOptions } =
    useRepoFilterOptions(
      workspaceSlug ?? '',
      isExpanded && isScopedToRepo,
      recentSearches
    );

  // Keep local query state in sync when initialQuery changes — either
  // because the URL ?q= param changed (e.g. browser history navigation)
  // or because the user navigated to/from a search route.
  useEffect(() => {
    setQuery(initialQuery);
    setActiveFilter(null);
  }, [initialQuery]);

  // When focus leaves an element, we defer closing the dialog to the
  // next tick. If focus moves to another element inside the SearchAnchor, its
  // onFocus fires and cancels the timeout, keeping the dialog open. If focus
  // leaves entirely, the timeout runs and the dialog closes.
  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = window.setTimeout(() => {
      setIsExpanded(false);
    });
  }, [setIsExpanded]);

  const handleFocus = useCallback(() => {
    window.clearTimeout(blurTimeoutRef.current);
    setIsExpanded(true);
  }, [setIsExpanded]);

  useEffect(() => {
    if (isExpanded) {
      inputRef.current?.focus();
    }
  }, [isExpanded]);

  // Update the query. Also clear activeFilter when the input becomes empty,
  // since there can be no active filter in an empty query. For non-empty inputs,
  // handleSelect fires after this and recomputes activeFilter from the DOM.
  const handleInput = useCallback((value: string) => {
    setQuery(value);
    if (!value) {
      setActiveFilter(null);
    }
  }, []);

  // React's onSelect fires on any caret/selection change (clicks, arrow keys,
  // typing), not just text highlighting. We read the current query and cursor
  // position directly from the DOM to avoid stale-closure issues.
  const handleSelect = useCallback(() => {
    const cursor = inputRef.current?.selectionStart ?? 0;
    const currentQuery = inputRef.current?.value ?? '';
    setActiveFilter(
      getActiveFilter(currentQuery, cursor, FILTERS_WITH_OPTIONS)
    );
  }, []);

  // Replaces the active `repo:` token in the query with the selected repo slug.
  // e.g. "query repo: more" → "query repo:frontbucket more"
  const handleRepoFilterOptionSelect = useCallback(
    (repoSlug: string) => {
      if (!activeFilter) {
        return;
      }

      // Split the query around the active filter token
      const before = query.slice(0, activeFilter.startIndex);
      const after = query.slice(activeFilter.endIndex);

      // Avoid a double space if text already follows the token
      const trailingSpace = after.startsWith(' ') ? '' : ' ';
      const newToken = `${activeFilter.filter.label}${repoSlug}${trailingSpace}`;

      // Reassemble the query — we know we're exiting scoped mode
      const newQuery = `${before}${newToken}${after}`;
      setQuery(newQuery);
      setActiveFilter(null);
      inputRef.current?.focus();
    },
    [activeFilter, query]
  );

  const handleEnter = useCallback(() => {
    inputRef.current?.blur();
    onSearch?.(query);
  }, [onSearch, query]);

  const handleClear = useCallback(() => {
    setQuery('');
    setActiveFilter(null);
  }, []);

  const handleFilterClick = useCallback(
    (filter: SearchFilter) => {
      const separator = query && !query.endsWith(' ') ? ' ' : '';
      const newQuery = `${query}${separator}${filter.label}`;
      setQuery(newQuery);
      if (FILTERS_WITH_OPTIONS.includes(filter)) {
        setActiveFilter(
          getActiveFilter(newQuery, newQuery.length, FILTERS_WITH_OPTIONS)
        );
      }
      inputRef.current?.focus();
    },
    [query]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsExpanded(false);
        inputRef.current?.blur();
      }
    },
    [setIsExpanded]
  );

  const filteredSearches = useMemo(
    () =>
      filterRecentSearches(recentSearches, query, MAX_VISIBLE_RECENT_SEARCHES),
    [recentSearches, query]
  );

  return (
    <Box role="search" xcss={containerStyles}>
      <SearchAnchor
        isExpanded={isExpanded}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        shouldFillContainer
      >
        <SearchInput
          ref={inputRef}
          isExpanded={isExpanded}
          shouldFillContainer
          placeholder={intl.formatMessage(messages.inputPlaceholder)}
          value={query}
          onInput={handleInput}
          onEnter={handleEnter}
          onClear={handleClear}
          onSelect={handleSelect}
          showClearButton={query.length > 0}
        />

        {isExpanded && (
          <KeyboardHighlightProvider listenerNode={inputRef.current}>
            <SearchDialog shouldFillContainer isExpanded>
              <ResultContainer>
                {isScopedToRepo ? (
                  <section
                    aria-label={intl.formatMessage(
                      messages.refineYourSearchRepository
                    )}
                  >
                    <SearchResultSection
                      title={intl.formatMessage(
                        messages.refineYourSearchRepository
                      )}
                    >
                      {isLoadingRepoFilterOptions ? (
                        <Box xcss={repoFilterLoadingContainerStyles}>
                          <Spinner
                            size="medium"
                            label={intl.formatMessage(
                              messages.loadingRepoFilterOptions
                            )}
                          />
                        </Box>
                      ) : (
                        repoFilterOptions.map(slug => (
                          <SearchResult
                            key={`repo-filter-option-${slug}`}
                            title={slug}
                            href="#"
                            icon={<AngleBracketsIcon label="" />}
                            onSelect={e => {
                              e.preventDefault();
                              handleRepoFilterOptionSelect(slug);
                            }}
                          />
                        ))
                      )}
                    </SearchResultSection>
                  </section>
                ) : (
                  <>
                    <section
                      aria-label={intl.formatMessage(messages.refineYourSearch)}
                    >
                      <SearchResultSection
                        title={intl.formatMessage(messages.refineYourSearch)}
                      >
                        {SEARCH_FILTERS.map(filter => (
                          <SearchResult
                            key={filter.label}
                            title={filter.label}
                            meta={
                              filter.description
                                ? intl.formatMessage(filter.description)
                                : undefined
                            }
                            href="#"
                            icon={<AddIcon label="" />}
                            onSelect={e => {
                              e.preventDefault();
                              handleFilterClick(filter);
                            }}
                          />
                        ))}
                      </SearchResultSection>
                    </section>

                    {filteredSearches.length > 0 && (
                      <section
                        aria-label={intl.formatMessage(
                          messages.recentlySearched
                        )}
                      >
                        <SearchResultSection
                          title={intl.formatMessage(messages.recentlySearched)}
                        >
                          {filteredSearches.map(search => (
                            <SearchResult
                              key={search.query}
                              title={search.query}
                              meta={
                                Date.now() - search.timestamp < 1000
                                  ? intl.formatMessage(messages.justViewed)
                                  : intl.formatMessage(
                                      messages.viewedRelativeDate,
                                      {
                                        relativeDate: formatRelativeDate(
                                          search.timestamp,
                                          intl
                                        ),
                                      }
                                    )
                              }
                              href="#"
                              icon={<ClockIcon label="" />}
                              onSelect={e => {
                                e.preventDefault();
                                onSearch?.(search.query);
                              }}
                            />
                          ))}
                        </SearchResultSection>
                      </section>
                    )}
                    {(isLoadingRepositories ||
                      recentRepositories.length > 0) && (
                      <section
                        aria-label={intl.formatMessage(
                          query
                            ? messages.repositories
                            : messages.recentlyUpdatedRepositories
                        )}
                      >
                        <SearchResultSection
                          title={intl.formatMessage(
                            query
                              ? messages.repositories
                              : messages.recentlyUpdatedRepositories
                          )}
                        >
                          {isLoadingRepositories ? (
                            <Box xcss={repoLoadingContainerStyles}>
                              <Spinner
                                size="medium"
                                label={intl.formatMessage(
                                  messages.loadingRepositories
                                )}
                              />
                            </Box>
                          ) : (
                            recentRepositories.map(repo => (
                              <SearchResult
                                key={`repo-${repo.id}`}
                                title={repo.name}
                                meta={
                                  repo.updatedOn
                                    ? intl.formatMessage(
                                        messages.updatedRelativeDate,
                                        {
                                          relativeDate: formatRelativeDate(
                                            new Date(repo.updatedOn).getTime(),
                                            intl
                                          ),
                                        }
                                      )
                                    : ''
                                }
                                href={repo.href}
                                icon={
                                  <Avatar
                                    src={repo.avatarHref}
                                    appearance="square"
                                    size="xsmall"
                                    name={repo.name}
                                  />
                                }
                                linkComponent={ComponentLink}
                                onSelect={() => {
                                  inputRef.current?.blur();
                                  setIsExpanded(false);
                                }}
                              />
                            ))
                          )}
                        </SearchResultSection>
                      </section>
                    )}
                  </>
                )}
              </ResultContainer>
              <SearchFooter>
                <BitbucketSearchLink
                  href={navigationUrls.ui.workspaceSearch(workspaceSlug, query)}
                  label={intl.formatMessage(messages.searchCodeInBitbucket)}
                  onClick={() => setIsExpanded(false)}
                />
              </SearchFooter>
            </SearchDialog>
          </KeyboardHighlightProvider>
        )}
      </SearchAnchor>
    </Box>
  );
};
